import useSWR from "swr";
import { useRouter } from "next/router";
import {
  getTradeInContent,
  getTradeInComponentHtml,
} from "@/services/trade-in";

/**
 * One request for the whole basket. The key is sorted and de-duplicated so
 * re-ordering the cart does not refetch. Only SKUs with a service reach `bySku`.
 *
 * `components` narrows what the BE sends; it is part of the key, so a caller
 * asking for one component never reads a cache entry filled by a caller that
 * asked for all four.
 */
export function useTradeInContent(skus, components) {
  const { locale } = useRouter();
  const list = [...new Set((skus || []).filter(Boolean))].sort();

  const res = useSWR(
    list.length
      ? ["trade-in-content", locale, list.join(","), components || "all"]
      : null,
    () => getTradeInContent(list, components),
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
    }
  );

  const bySku = {};
  for (const row of res?.data?.data?.data || []) {
    if (row?.service_id != null) bySku[row.sku] = row;
  }

  return { bySku, isLoading: !res?.error && !res?.data };
}

/**
 * The URL encodes service and store view, so items sharing a service share one
 * request. `enabled` defers the popup until it is opened.
 */
export function useTradeInComponentHtml(url, enabled = true) {
  const res = useSWR(
    url && enabled ? ["trade-in-html", url] : null,
    () => getTradeInComponentHtml(url),
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
    }
  );

  return {
    html: res?.data || null,
    isLoading: Boolean(url && enabled) && !res?.error && !res?.data,
    isError: Boolean(res?.error),
  };
}
